import React, { useCallback } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  defineMessages,
  injectIntl,
  FormattedMessage,
} from '@techstyle/react-intl';

const maxQuantity = config.get('public.quantityRules.maxSelectableQuantity');

const messages = defineMessages({
  quantity: {
    defaultMessage: 'Quantity',
    id: 'global_cta.quantity',
  },
  decrement: {
    defaultMessage: 'Decrement Quantity',
    id: 'global_cta.decrement_quantity',
  },
  increment: {
    defaultMessage: 'Increment Quantity',
    id: 'global_cta.increment_quantity',
  },
});

const QuantityContainer = styled.div`
  display: flex;
  margin-top: 8px 0px;
`;
const QuantityValue = styled.div`
  width: 26px;
  height: 22px;
  margin: 0 10px;
  font-size: 16px;
  line-height: 1.5;
  text-align: center;
`;

const StyledButton = styled.button`
  width: 32px;
  min-width: 32px;
  height: 32px;
  border-radius: 1px;
  font-size: 29px;
  font-weight: 300;

  border: solid 1px ${props => props.theme.colors.stone};

  &:disabled {
    color: ${props => props.theme.colors.subdued};
  }
`;

const ButtonLabel = styled.div`
  height: 100%;
  line-height: 25px;
`;

const MinimumQuantityLabel = styled.span`
  color: ${props => props.theme.colors.subdued};
  margin-bottom: ${props => props.theme.spacing.tiny}px;
`;

function QuantityCounterSelect({
  availableQuantity,
  onChange,
  minimumQuantity = 1,
  value = 1,
  intl,
}) {
  const maxAvailableQty = availableQuantity
    ? Math.max(0, Math.min(availableQuantity, maxQuantity))
    : maxQuantity;

  const increment = useCallback(() => {
    if (value < maxAvailableQty) {
      const newQuantity = value + 1;
      if (onChange) {
        onChange(newQuantity);
      }
    }
  }, [maxAvailableQty, onChange, value]);

  const decrement = useCallback(() => {
    if (value >= 2) {
      const newQuantity = value - 1;
      if (onChange) {
        onChange(newQuantity);
      }
    }
  }, [onChange, value]);

  return (
    <>
      {minimumQuantity > 1 && (
        <MinimumQuantityLabel>
          <FormattedMessage
            defaultMessage="Minimum quantity: {minimumQuantity}"
            id="global_cta.minimum_quantity"
            values={{ minimumQuantity }}
          />
        </MinimumQuantityLabel>
      )}
      <QuantityContainer
        data-autotag="pdp-choose-quantity"
        aria-label={intl.formatMessage(messages.quantity)}
      >
        <StyledButton
          disabled={value <= minimumQuantity}
          white
          onClick={decrement}
          aria-label={intl.formatMessage(messages.decrement)}
          data-autotag="quantityDecrease"
        >
          <ButtonLabel>-</ButtonLabel>
        </StyledButton>
        <QuantityValue data-autotag="pdpQuantityValue">{value}</QuantityValue>
        <StyledButton
          white
          onClick={increment}
          aria-label={intl.formatMessage(messages.increment)}
          data-autotag="quantityIncrease"
        >
          <ButtonLabel>+</ButtonLabel>
        </StyledButton>
      </QuantityContainer>
    </>
  );
}

QuantityCounterSelect.propTypes = {
  availableQuantity: PropTypes.number,
  onChange: PropTypes.func,
  value: PropTypes.number,
  intl: PropTypes.object,
};
QuantityCounterSelect.QuantityValue = QuantityValue;

export default injectIntl(QuantityCounterSelect);
